/**
 * @fileoverview Custom React Query Mutation Hook for API Requests
 *
 * This module provides a custom hook that wraps React Query's useMutation
 * with enhanced functionality for mutation operations in React applications. Features include:
 *
 * - **useCustomMutation**: Enhanced mutation hook for POST/PUT/PATCH/DELETE operations
 * - **Integrated Error Handling**: Automatic error notifications via snackbar system
 * - **CSRF Protection**: Automatic CSRF token inclusion in requests
 * - **Response Caching**: Configurable cache policies for performance optimization
 * - **Retry Logic**: Intelligent retry mechanisms with exponential backoff
 * - **Component Safety**: Mount status checking to prevent memory leaks
 * - **Event Tracking**: Error event logging for monitoring and analytics
 * - **Dynamic URL Resolution**: Support for both static URLs and function-based URL generation
 *
 * The hook integrates with the application's notification system, error tracking,
 * and provides enhanced developer experience with proper TypeScript support.
 *
 * @version 1.0.0
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useRef } from "react";
import { useGetIsMounted } from "rooks";

import { RETRY_DELAY } from "config/constants";
import { usePostEvent } from "hooks/usePostEvent/usePostEvent";
import { getCookie } from "utils/cookies";
import { objectToFormData } from "utils/form-data";

import { fetchWithRefresh } from "./sessionRefresh/refresh";
import { ApiError, BaseMutationOptions, CachePolicy } from "./types";
import { getIsLoggingOut, handleResponse, handleRetry } from "./utils";

/**
 * Type definition for URL parameter in mutation hooks.
 *
 * @template V - The type of variables passed to the mutation
 */
type UrlOrFn<V> = string | ((variables: V) => string);

/**
 * Custom hook for HTTP mutations (POST, PUT, PATCH, DELETE) with enhanced error handling.
 *
 * This hook wraps React Query's useMutation with additional functionality:
 * - Automatic JSON serialization for request bodies
 * - Configurable HTTP methods (POST, PUT, PATCH, DELETE)
 * - Dynamic URL resolution (string or function-based)
 * - Automatic error handling and user notifications
 * - CSRF token inclusion for security
 * - Intelligent retry logic with exponential backoff
 * - Response object access for additional processing
 * - Component mount status checking to prevent memory leaks
 *
 * @template T - The expected type of the response data
 * @template V - The type of variables/payload passed to the mutation
 * @param url - Static URL string or function that generates URL from variables
 * @param method - HTTP method to use for the mutation
 * @param options - Configuration options for the mutation behavior
 *
 * @returns Enhanced mutation result object with response access
 *
 * @example
 * ```typescript
 * // Basic POST mutation
 * const createUser = useCustomMutation<User, CreateUserRequest>(
 *   "/api/users",
 *   "POST"
 * );
 *
 * const handleCreateUser = async () => {
 *   try {
 *     const newUser = await createUser.mutateAsync({
 *       name: "John Doe",
 *       email: "john@example.com"
 *     });
 *     console.log("User created:", newUser);
 *   } catch (error) {
 *     console.error("Failed to create user:", error);
 *   }
 * };
 *
 * // Dynamic URL with variables
 * const updateUser = useCustomMutation<User, UpdateUserRequest>(
 *   (variables) => `/api/users/${variables.id}`,
 *   "PUT",
 *   {
 *     retry: 2,
 *     shouldShowErrorSnackbar: (response) => response.status >= 500
 *   }
 * );
 *
 * // DELETE mutation
 * const deleteUser = useCustomMutation<void, { id: string }>(
 *   (variables) => `/api/users/${variables.id}`,
 *   "DELETE"
 * );
 * ```
 */
export const useCustomMutation = <T, V extends Object | void | undefined, TError = unknown>(
  url: UrlOrFn<V>,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  options: BaseMutationOptions<T, V> = {},
  excludeKeys: string[] = []
) => {
  const { enqueueSnackbar } = useSnackbar();
  const { sendErrorEvent } = usePostEvent();
  const getIsMounted = useGetIsMounted();
  const queryClient = useQueryClient();
  const {
    cachePolicy = CachePolicy.NO_CACHE,
    retry = 0,
    cacheKeysToInvalidate,
    onSuccess: userOnSuccess,
    ...restOptions
  } = options;

  // Ref to keep the last response of the mutation
  const responseRef = useRef<Response | undefined>();
  const mutationResult = useMutation<T, ApiError<TError>, V>({
    ...restOptions,
    onSuccess: (data, variables, context) => {
      // Automatically invalidate cache keys if specified
      if (cacheKeysToInvalidate && cacheKeysToInvalidate.length > 0) {
        cacheKeysToInvalidate.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }

      // Call user's onSuccess if provided
      if (userOnSuccess) {
        userOnSuccess(data, variables, context);
      }
    },
    mutationFn: async (variables: V) => {
      if (getIsLoggingOut()) {
        throw new Error("Session expired — redirecting to login");
      }
      const resolvedUrl = typeof url === "function" ? url(variables) : url;

      // exclude keys from the body if needed
      let bodyData = variables;
      if (excludeKeys.length > 0 && method !== "DELETE") {
        const filteredData = { ...variables } as any;
        excludeKeys.forEach((key) => delete filteredData[key]);
        bodyData = filteredData;
      }

      const hasFiles = bodyData ? Object.values(bodyData).some((item) => item instanceof File) : false;
      const serializedBody = hasFiles && bodyData ? objectToFormData(bodyData) : JSON.stringify(bodyData);

      const response = await fetchWithRefresh(resolvedUrl, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": cachePolicy,
          Pragma: cachePolicy,
          "X-CSRFToken": getCookie("csrftoken") || "",
          ...(hasFiles && {
            "Content-Type": "multipart/form-data; boundary=----",
          }),
        },
        body: method !== "DELETE" ? serializedBody : undefined,
      });
      // Clone the response to keep it for later use
      responseRef.current = response.clone();

      return handleResponse<T>(response, enqueueSnackbar);
    },
    retry: (failureCount: number, error: ApiError<TError>) =>
      handleRetry(options, error, responseRef, enqueueSnackbar, getIsMounted, sendErrorEvent, failureCount, retry),
    retryDelay: (failureCount: number): number => RETRY_DELAY * (failureCount + 1),
  });

  return {
    ...mutationResult,
    response: responseRef.current,
  };
};
